import { useState, useEffect } from 'react';
import { EditorLayout, EditorSidebarSection, EditorSectionCard, EditorSectionItem } from '@/components/editor';
import { UseFormReturn } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { QuickDatePicker } from '@/components/events/QuickDatePicker';
import { useMediaAssets } from '@/hooks/useMediaAssets';
import { listAssetsByDraftSession, resolveAssetUrlAsync, resolveAssetUrl } from '@/lib/media/mediaApi';
import { StepperField } from '@/components/ui/stepper-field';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import {
  Calendar,
  FileText,
  Image,
  MapPin,
  Tag,
  DollarSign,
  Settings,
  Plus,
  Ticket,
  Eye,
  GraduationCap,
  Wine,
  ShoppingBasket,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WhenSection } from './WhenSection';
import { LocationSection } from './LocationSection';
import { ActivityImagesSection } from './sections/ActivityImagesSection';
import { ActivityTagSelector } from './ActivityTagSelector';
import { LearningPointsEditor } from './LearningPointsEditor';
import { ActivityPagePreview } from './ActivityPagePreview';

import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ENABLE_EVENT_BOOKING_UI } from '@/lib/activities/featureFlags';
import type { EventFormData } from './eventFormSchema';
import type { MediaAsset } from '@/lib/media/mediaTypes';

/* ── Upload hook shape (matches useMediaUpload return) ── */
interface UploadHook {
  isUploading: boolean;
  progress: number;
  errors: string[];
  uploadFiles: (files: File[]) => Promise<MediaAsset[]>;
  cancel: () => void;
}

/* ── Summary helpers ── */

export function whenSummary(form: UseFormReturn<EventFormData>): string {
  const mode = form.watch('timing_mode');
  const eventDate = form.watch('event_date');
  const startDate = form.watch('start_date');
  const endDate = form.watch('end_date');
  const startTime = form.watch('start_time');
  const endTime = form.watch('end_time');
  const isRecurring = form.watch('is_recurring');
  const selectedDates = form.watch('selected_dates');
  const selectedSeasons = form.watch('selected_seasons');

  if (mode === 'open' || mode === 'anytime') {
    if (startDate && endDate) return `Öppet ${startDate}–${endDate}`;
    if (startDate) return `Öppet från ${startDate}`;
    return 'Öppet under säsong';
  }

  if (mode === 'season') {
    if (selectedSeasons && selectedSeasons.length > 0) {
      return `Bäst: ${selectedSeasons.join(', ').toLowerCase()}`;
    }
    return 'Bäst under säsong';
  }

  if (isRecurring && selectedDates && selectedDates.length > 0) {
    return `${selectedDates.length} datum valda`;
  }

  if (!eventDate) return 'Ingen tid vald ännu';

  try {
    const d = new Date(eventDate);
    const dateStr = format(d, 'EEE d MMM', { locale: sv });
    if (startTime && endTime && endTime.trim()) return `${dateStr} · ${startTime}–${endTime}`;
    if (startTime) return `${dateStr} · ${startTime}`;
    return dateStr;
  } catch {
    return eventDate;
  }
}

function descriptionSummary(form: UseFormReturn<EventFormData>): string {
  const full = form.watch('description');
  if (full && full.trim()) return 'Beskrivning tillagd';
  return 'Lägg till en beskrivning';
}

function locationSummary(form: UseFormReturn<EventFormData>): string {
  const useProducer = form.watch('use_producer_address');
  const custom = form.watch('custom_address');
  if (!useProducer && custom && custom.trim()) {
    return custom.length > 40 ? custom.slice(0, 37) + '…' : custom;
  }
  return 'Hos värden';
}

function priceSummary(form: UseFormReturn<EventFormData>): string {
  const isFree = form.watch('is_free');
  if (isFree) return 'Gratis';
  const price = form.watch('price_sek');
  if (price && price > 0) return `${price} kr/person`;
  return 'Inget pris satt';
}

function tagsSummary(form: UseFormReturn<EventFormData>): string {
  const tags = form.watch('outing_tags') || [];
  const seasons = form.watch('selected_seasons') || [];
  const total = tags.length + seasons.length;
  if (total === 0) return 'Lägg till taggar';
  return `${total} tagg${total > 1 ? 'ar' : ''}`;
}


function bookingSummary(form: UseFormReturn<EventFormData>): string {
  const mode = form.watch('booking_mode');
  if (mode === 'required') return 'Bokning krävs';
  if (mode === 'optional') return 'Anmälan valfri';
  return 'Ingen bokning';
}

function audienceSummary(form: UseFormReturn<EventFormData>): string {
  const audience = form.watch('audience');
  return audience === 'authenticated' ? 'Synlighet: Endast inloggade' : 'Synlighet: Alla';
}

function imagesSummaryFn(coverCount: number, galleryCount: number, mode: 'new' | 'edit'): string {
  const total = coverCount + galleryCount;
  if (total === 0) return mode === 'new' ? 'Lägg till bilder (valfritt)' : 'Inga bilder';
  return `${total} bild${total > 1 ? 'er' : ''}`;
}

/* ── Main Composer ── */

interface ActivityComposerProps {
  form: UseFormReturn<EventFormData>;
  mode: 'new' | 'edit';
  eventId?: string | null;
  draftSessionId: string;
  producerId?: string;
  requiresDate: boolean;
  isPublished: boolean;
  publishBlock?: { title: string; text: string; cta?: { route: string; label: string } } | null;
  coverUpload: UploadHook;
  galleryUpload: UploadHook;
  isSaving?: boolean;
  producerHasFarmSale?: boolean;
  producerHasAlcohol?: boolean;
  /** DEV only: raw producer capability fields for debugging */
  devProducerCapDebug?: { has_farm_sale_alcohol: boolean; has_farm_shop: boolean } | null;
  /** Template label shown as a "starting point" hint */
  templateLabel?: string | null;
  onChangeTemplate?: () => void;
}

export function ActivityComposer({
  form,
  mode,
  eventId,
  draftSessionId,
  coverUpload,
  galleryUpload,
  isSaving,
  producerId,
  requiresDate,
  isPublished,
  publishBlock,
  producerHasFarmSale = false,
  producerHasAlcohol = false,
  devProducerCapDebug,
  templateLabel,
  onChangeTemplate,
}: ActivityComposerProps) {
  const [previewCoverUrl, setPreviewCoverUrl] = useState<string | null>(null);
  const { data: coverAssets = [] } = useMediaAssets('event', eventId, 'cover');
  const { data: galleryAssets = [] } = useMediaAssets('event', eventId, 'gallery');

  // Draft-aware cover for preview: query draft session assets when no eventId yet
  const { data: draftAssets = [] } = useQuery({
    queryKey: ['media-assets-draft', draftSessionId],
    queryFn: () => listAssetsByDraftSession(draftSessionId),
    enabled: !eventId && !!draftSessionId,
  });

  // Resolve cover image URL from either finalized or draft assets
  const draftCover = draftAssets.find(a => a.kind === 'cover');
  const finalizedCover = coverAssets.length > 0
    ? (coverAssets.length === 1 ? coverAssets[0] : [...coverAssets].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0])
    : null;
  const previewCoverAsset = finalizedCover ?? draftCover ?? null;

  useEffect(() => {
    if (!previewCoverAsset) { setPreviewCoverUrl(null); return; }
    const syncUrl = resolveAssetUrl(previewCoverAsset, 640);
    if (syncUrl) { setPreviewCoverUrl(syncUrl); return; }
    resolveAssetUrlAsync(previewCoverAsset, 640).then(url => setPreviewCoverUrl(url));
  }, [previewCoverAsset?.id, previewCoverAsset?.path_640]);

  const imagesSummary = imagesSummaryFn(coverAssets.length, galleryAssets.length, mode);

  // ── Shared preview data ──
  const title = form.watch('title');
  const description = form.watch('description');
  const eventDate = form.watch('event_date');
  const openStartDate = form.watch('start_date');
  const openEndDate = form.watch('end_date');
  const startTimeVal = form.watch('start_time');
  const endTimeVal = form.watch('end_time');
  const isFree = form.watch('is_free');
  const priceSek = form.watch('price_sek');
  const timingMode = form.watch('timing_mode');
  const selectedSeasons = form.watch('selected_seasons');
  const bookingMode = form.watch('booking_mode');
  const eventType = form.watch('event_type');
  const maxSpots = form.watch('max_spots');
  const useProducerAddress = form.watch('use_producer_address');
  const customAddress = form.watch('custom_address');

  const locationText = !useProducerAddress && customAddress?.trim()
    ? customAddress
    : 'Hos värden';

  /* ── Page-like preview (used for both modes) ── */
  const pagePreview = (
    <ActivityPagePreview
      title={title}
      description={description}
      coverImageUrl={previewCoverUrl}
      eventDate={timingMode === 'open' ? openStartDate : eventDate}
      endDate={timingMode === 'open' ? openEndDate : undefined}
      startTime={startTimeVal}
      endTime={endTimeVal}
      timingMode={timingMode}
      selectedSeasons={selectedSeasons}
      isFree={isFree}
      priceSek={priceSek}
      locationText={locationText}
      bookingMode={bookingMode}
      eventType={eventType}
      maxSpots={maxSpots}
    />
  );

  /* ── Settings sections (shared between modes) ── */
  const settingsSections = (
    <EditorSectionCard>
      <EditorSectionItem title="Tid" icon={Calendar} hint={whenSummary(form)}>
        {requiresDate && (
          <div className="space-y-2 mb-4">
            <p className="text-sm text-muted-foreground">Snabbval</p>
            <QuickDatePicker form={form} />
          </div>
        )}
        <WhenSection form={form} requiresDate={requiresDate} />
      </EditorSectionItem>

      <EditorSectionItem title="Plats" icon={MapPin} hint={locationSummary(form)}>
        <LocationSection form={form} />
      </EditorSectionItem>

      <EditorSectionItem title="Pris" icon={DollarSign} hint={priceSummary(form)}>
        <PriceFields form={form} />
      </EditorSectionItem>

      <EditorSectionItem title="Bokning" icon={Ticket} hint={bookingSummary(form)} testId="section-booking-trigger">
        <BookingSection form={form} />
      </EditorSectionItem>

      <EditorSectionItem title="Synlighet" icon={Eye} hint={audienceSummary(form)}>
        <AudienceSection form={form} />
      </EditorSectionItem>

      <EditorSectionItem title={`Typ: ${eventType === 'course' ? 'Kurs' : 'Aktivitet'}`} icon={GraduationCap} hint={eventType === 'course' ? 'Kurs' : 'Aktivitet'}>
        <EventTypeSection form={form} />
        {producerHasFarmSale && <FarmSaleSection form={form} showAlcohol={producerHasAlcohol} />}
      </EditorSectionItem>

      <EditorSectionItem title="Taggar" icon={Tag} hint={tagsSummary(form)}>
        <TagFields form={form} />
      </EditorSectionItem>

      {ENABLE_EVENT_BOOKING_UI && (
        <EditorSectionItem title="Avancerat" icon={Settings} hint="Avancerat">
          <AdvancedFields form={form} />
        </EditorSectionItem>
      )}
    </EditorSectionCard>
  );

  /* ── Content sections (description + images) ── */
  const contentSections = (
    <EditorSectionCard>
      <EditorSectionItem title="Beskrivning" icon={FileText} hint={descriptionSummary(form)}>
        <DescriptionFields form={form} />
      </EditorSectionItem>
      <EditorSectionItem title="Bilder" icon={Image} hint={imagesSummary} testId="section-images-trigger">
        <ActivityImagesSection
          coverUpload={coverUpload}
          galleryUpload={galleryUpload}
          eventId={eventId}
          producerId={producerId}
          draftSessionId={draftSessionId}
          disabled={isSaving}
        />
      </EditorSectionItem>
    </EditorSectionCard>
  );

  if (mode === 'new') {
    /* ── CREATE: Title input above EditorLayout, then preview + sidebar ── */
    return (
      <div className="space-y-6">
        {/* Title – prominent, editorial */}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">Titel</FormLabel>
              <FormControl>
                <Input
                  placeholder="Vad ska aktiviteten heta?"
                  className="text-xl font-semibold h-14 border-0 border-b-2 border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
                  autoFocus
                  data-testid="event-title"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Template starting point hint */}
        {templateLabel && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Startpunkt: <span className="font-medium text-foreground">{templateLabel}</span></span>
            {onChangeTemplate && (
              <button
                type="button"
                onClick={onChangeTemplate}
                className="text-xs underline underline-offset-2 hover:text-foreground transition-colors"
              >
                Byt
              </button>
            )}
          </div>
        )}

        <EditorLayout
          main={pagePreview}
          sidebar={
            <div className="space-y-6">
              <EditorSidebarSection heading="Innehåll">
                {contentSections}
              </EditorSidebarSection>
              <EditorSidebarSection heading="Inställningar">
                {settingsSections}
              </EditorSidebarSection>
            </div>
          }
        />
      </div>
    );
  }

  // ── EDIT mode: preview-first with full sidebar ──
  return (
    <div className="space-y-6">
      {/* Title – prominent, always visible */}
      <FormField
        control={form.control}
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="sr-only">Titel</FormLabel>
            <FormControl>
              <Input
                placeholder="Namn på aktiviteten"
                className="text-xl font-semibold h-14 border-0 border-b-2 border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
                data-testid="event-title"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <EditorLayout
        main={pagePreview}
        sidebar={
          <div className="space-y-6">
            <EditorSidebarSection heading="Innehåll">
              {contentSections}
            </EditorSidebarSection>
            <EditorSidebarSection heading="Inställningar">
              {settingsSections}
            </EditorSidebarSection>
          </div>
        }
      />
    </div>
  );
}

/* ── Event Type Selector ── */

const EVENT_TYPE_OPTIONS = [
  { value: 'event', label: 'Aktivitet', desc: 'En upplevelse, middag, workshop eller liknande.' },
  { value: 'course', label: 'Kurs', desc: 'En aktivitet där lärande är huvudsyftet.' },
] as const;

function EventTypeSection({ form }: { form: UseFormReturn<EventFormData> }) {
  return (
    <div className="rounded-lg border p-4 space-y-3">
      <FormField
        control={form.control}
        name="event_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2 text-sm font-medium">
              <GraduationCap className="h-4 w-4 text-muted-foreground" />
              Vad vill du skapa?
            </FormLabel>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={(val) => {
                  const prev = field.value;
                  field.onChange(val);
                  if (val === 'course' && prev !== 'course') {
                    const currentBookingMode = form.getValues('booking_mode');
                    if (currentBookingMode === 'none') {
                      form.setValue('booking_mode', 'required', { shouldDirty: true });
                    }
                  }
                }}
                className="space-y-2"
              >
                {EVENT_TYPE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    htmlFor={`event-type-${opt.value}`}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border p-3.5 cursor-pointer transition-colors',
                      'hover:bg-muted/50 hover:border-border',
                      field.value === opt.value && 'border-primary bg-primary/5',
                    )}
                  >
                    <RadioGroupItem value={opt.value} id={`event-type-${opt.value}`} className="mt-0.5" />
                    <div className="flex-1">
                      <span className="font-medium text-sm">{opt.label}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

/* ── Farm Sale Section (conditional on producer capability) ── */

function FarmSaleSection({ form, showAlcohol }: { form: UseFormReturn<EventFormData>; showAlcohol: boolean }) {
  const includesFarmSale = form.watch('includes_farm_sale');
  const includesAlcohol = form.watch('includes_farm_sale_alcohol');

  return (
    <div className="rounded-lg border border-dashed p-4 space-y-3 mt-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Extra för den här aktiviteten</p>

      <FormField
        control={form.control}
        name="includes_farm_sale"
        render={({ field }) => (
          <FormItem className="space-y-1">
            <div className="flex flex-row items-start gap-3">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    if (!checked) {
                      form.setValue('includes_farm_sale_alcohol', false, { shouldDirty: true });
                    }
                  }}
                  className="mt-0.5"
                />
              </FormControl>
              <div className="space-y-0.5">
                <FormLabel className="text-sm flex items-center gap-1"><ShoppingBasket aria-hidden className="h-4 w-4 shrink-0" /> Handla från gården</FormLabel>
                <FormDescription className="text-xs">
                  Markera om besökare kan köpa produkter i samband med aktiviteten.
                </FormDescription>
              </div>
            </div>
          </FormItem>
        )}
      />

      {showAlcohol && (
        <FormField
          control={form.control}
          name="includes_farm_sale_alcohol"
          render={({ field }) => (
            <FormItem className="space-y-1 pl-6">
              <div className="flex flex-row items-start gap-3">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      if (checked) {
                        form.setValue('includes_farm_sale', true, { shouldDirty: true });
                      }
                    }}
                    className="mt-0.5"
                  />
                </FormControl>
                <div className="space-y-0.5">
                  <FormLabel className="text-sm flex items-center gap-1"><Wine aria-hidden className="h-4 w-4 shrink-0" /> Dryck (vin, cider, öl)</FormLabel>
                  <FormDescription className="text-xs">
                    Markera om besökare kan köpa dryck (t.ex. vin, cider eller öl) i samband med aktiviteten.
                  </FormDescription>
                </div>
              </div>
            </FormItem>
          )}
        />
      )}

      {(includesFarmSale || includesAlcohol) && (
        <div className="rounded-lg border border-dashed border-muted-foreground/25 bg-muted/30 px-3 py-2 space-y-1">
          <p className="text-xs text-muted-foreground">Så här visas det för besökare</p>
          {includesFarmSale && (
            <div className="flex items-center gap-2 text-sm text-foreground/80">
              <ShoppingBasket aria-hidden className="h-4 w-4 shrink-0" />
              <span>Handla från gården i samband med aktiviteten</span>
            </div>
          )}
          {includesAlcohol && (
            <div className="flex items-center gap-2 text-sm text-foreground/80">
              <Wine aria-hidden className="h-4 w-4 shrink-0" />
              <span>Dryck i samband med aktiviteten</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Inline field groups ── */

function DescriptionFields({ form }: { form: UseFormReturn<EventFormData> }) {
  const eventType = form.watch('event_type');
  const isCourse = eventType === 'course';

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Beskriv aktiviteten</FormLabel>
            <FormDescription>
              Berätta kort vad som händer och vad gästerna kan förvänta sig.
            </FormDescription>
            <FormControl>
              <RichTextEditor
                value={field.value ?? ''}
                onChange={field.onChange}
                preset="simple"
                minHeightPx={140}
                placeholder="Beskriv aktiviteten..."
                expandable
                expandedTitle="Beskriv aktiviteten"
                expandedSurface="activity_composer"
                expandedField="description"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {isCourse && (
        <FormField
          control={form.control}
          name="learning_points"
          render={({ field }) => (
            <LearningPointsEditor
              value={field.value || []}
              onChange={field.onChange}
            />
          )}
        />
      )}
    </div>
  );
}

function PriceFields({ form }: { form: UseFormReturn<EventFormData> }) {
  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="is_free"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <FormLabel className="text-sm">Gratis</FormLabel>
              <FormDescription className="text-xs">Kostnadsfritt att delta</FormDescription>
            </div>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                  if (checked) {
                    form.setValue('price_sek', 0);
                  }
                }}
              />
            </FormControl>
          </FormItem>
        )}
      />
      {!form.watch('is_free') && (
        <FormField
          control={form.control}
          name="price_sek"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pris per person</FormLabel>
              <div className="relative">
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    placeholder="0"
                    className="pr-10"
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                  />
                </FormControl>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr</span>
              </div>
              <FormDescription className="text-xs">Betalning sker på plats.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

    </div>
  );
}

function TagFields({ form }: { form: UseFormReturn<EventFormData> }) {
  return (
    <FormField
      control={form.control}
      name="outing_tags"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Taggar</FormLabel>
          <FormControl>
            <ActivityTagSelector
              value={field.value || []}
              onChange={field.onChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

const AUDIENCE_OPTIONS = [
  { value: 'public', label: 'Publik', desc: 'Alla kan se och hitta aktiviteten.' },
  { value: 'authenticated', label: 'Endast inloggade', desc: 'Bara inloggade Farmly-medlemmar kan se detaljer.' },
] as const;

function AudienceSection({ form }: { form: UseFormReturn<EventFormData> }) {
  return (
    <FormField
      control={form.control}
      name="audience"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Synlighet</FormLabel>
          <FormControl>
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="space-y-2"
            >
              {AUDIENCE_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value={opt.value} id={`audience-${opt.value}`} className="mt-0.5" />
                  <Label htmlFor={`audience-${opt.value}`} className="cursor-pointer flex-1">
                    <span className="font-medium text-sm">{opt.label}</span>
                    <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

const BOOKING_MODE_OPTIONS = [
  { value: 'none', label: 'Ingen bokning', desc: 'Besökare kan bara läsa och komma förbi.', testId: 'booking-mode-none' },
  { value: 'optional', label: 'Anmälan valfri', desc: 'Besökare kan anmäla intresse så du vet ungefär.', testId: 'booking-mode-optional' },
  { value: 'required', label: 'Bokning krävs', desc: 'Besökare behöver boka plats.', testId: 'booking-mode-required' },
] as const;

function BookingSection({ form }: { form: UseFormReturn<EventFormData> }) {
  const bookingMode = form.watch('booking_mode');
  const eventType = form.watch('event_type');
  const isCourse = eventType === 'course';
  const isFree = form.watch('is_free');
  const priceSek = form.watch('price_sek');
  const hasPriceButNoBooking = isCourse && !isFree && priceSek > 0 && bookingMode === 'none';
  const courseNoBooking = isCourse && bookingMode === 'none';

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="booking_mode"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Bokningsläge</FormLabel>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="space-y-2"
              >
                {BOOKING_MODE_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                    <RadioGroupItem value={opt.value} id={`booking-${opt.value}`} className="mt-0.5" />
                    <Label htmlFor={`booking-${opt.value}`} className="cursor-pointer flex-1" data-testid={opt.testId}>
                      <span className="font-medium text-sm">{opt.label}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {courseNoBooking && (
        <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded-md p-2.5">
          {hasPriceButNoBooking
            ? 'Du har pris satt men ingen bokning. Överväg "Bokning krävs" för en bättre kursupplevelse.'
            : 'Kurser blir oftast bäst med bokning och begränsat antal platser.'}
        </p>
      )}

      {bookingMode !== 'none' && (
        <div className="space-y-4 pt-2 border-t">
          <StepperField
            form={form}
            name="max_spots"
            label="Antal platser"
            description={isCourse ? 'Max antal deltagare för hela kursen' : 'Max antal deltagare'}
            min={1}
            max={500}
          />
          <StepperField
            form={form}
            name="booking_deadline_hours"
            label="Anmälan stänger (timmar före start)"
            description="Antal timmar innan start som anmälan stängs"
            min={0}
            max={168}
          />
        </div>
      )}
    </div>
  );
}

function AdvancedFields({ form }: { form: UseFormReturn<EventFormData> }) {
  return (
    <div className="space-y-4">
      <StepperField
        form={form}
        name="max_spots"
        label="Antal platser"
        description="Max antal deltagare"
        min={1}
        max={500}
      />
      <StepperField
        form={form}
        name="booking_deadline_hours"
        label="Anmälan stänger (timmar före start)"
        description="Antal timmar innan start som anmälan stängs"
        min={0}
        max={168}
      />
    </div>
  );
}
